import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { getAccountKeyForMailboxInfo, type MailboxInfo } from "owa-client-types";
import { isUnifiedInboxMailboxInfo } from "owa-client-types/lib/UnifiedInboxMailboxInfo";
import { AccountStatus, App, BootState, getAccounts } from "owa-account-source-list-store";
import { isConsumer } from "owa-session-store";
import { lazyFetchMicrosoft365UpgradeEligibility, lazyHasActiveMicrosoft365Subscription, lazyGetMicrosoft365UpsellDimeConfig, lazyGetMicrosoft365UpgradeTier, type Microsoft365UpsellDimeConfig, type Microsoft365UpsellTarget, } from "owa-header-microsoft365-upsell";
import { lazyGetOutlookHeaderM365UpsellButtonIrisContent, lazyLoadOutlookHeaderM365UpsellButtonIrisContent, lazyRefreshOutlookHeaderM365UpsellButtonIrisContent, type OutlookHeaderM365UpsellButtonContent, } from "owa-header-microsoft365-upsell-iris";
import { shouldShowUpsellOption } from "owa-storage-common/lib/utils/shouldShowUpsellOption";
import { type EventArea, logUsage } from "owa-analytics";
import { getGrowthTelemetryData } from "owa-growth-telemetry";
import { createStore } from "owa-satcheljs";
import { mutatorAction } from "satcheljs";

/**
 * The upsell shown by the persistent Microsoft 365 header button, or `hidden` when it should not
 * show at all. The upsell is only available when every signed-in account is a consumer account;
 * within those sessions, two independent upsells share the one button, each behind its own flight:
 *  - `buy`: "Buy Microsoft 365" (header-persistentBuyM365Upsell) for consumers with no subscription.
 *  - `upgradeBasic`: "Upgrade your plan" for Microsoft 365 Basic subscribers.
 *  - `upgradePersonalOrFamily`: the same button for Personal or Family subscribers.
 * Both upgrade modes are gated by header-persistentUpgradeM365Upsell but route to separate DIME
 * configurations.
 */
export type Microsoft365UpsellMode = "hidden" | Microsoft365UpsellTarget;

type Microsoft365UpgradeTier = "basic" | "personalOrFamily";

type GetMicrosoft365UpgradeTier = (mailboxInfo: MailboxInfo) => Microsoft365UpgradeTier | undefined;
type HasActiveMicrosoft365Subscription = (mailboxInfo: MailboxInfo) => boolean;
type GetMicrosoft365UpsellDimeConfig = (target: Microsoft365UpsellTarget, irisContent?: OutlookHeaderM365UpsellButtonContent) => Microsoft365UpsellDimeConfig;

interface Microsoft365UpsellResource {
    dimeConfig?: Microsoft365UpsellDimeConfig;
    eligibilityGeneration: number;
    forceEligibilityRefresh?: boolean;
    getUpgradeTier?: GetMicrosoft365UpgradeTier;
    hasActiveMicrosoft365Subscription?: HasActiveMicrosoft365Subscription;
    irisContent?: OutlookHeaderM365UpsellButtonContent;
    irisContentPromise?: Promise<void>;
    irisRefreshPromise?: Promise<void>;
    irisRefreshTimeoutId?: number;
    irisVisibilityChangeHandler?: () => void;
    visibilityPromise?: Promise<boolean>;
    mode: Microsoft365UpsellMode;
}

interface Microsoft365UpsellRefreshStore {
    refreshVersionByAccount: Map<ReturnType<typeof getAccountKeyForMailboxInfo>, number>;
}

const getMicrosoft365UpsellRefreshStore = createStore<Microsoft365UpsellRefreshStore>("Microsoft365UpsellRefreshStore", {
    refreshVersionByAccount: new Map(),
});

const upsellResources = new Map<ReturnType<typeof getAccountKeyForMailboxInfo>, Microsoft365UpsellResource>();
const IRIS_CONTENT_REFRESH_INTERVAL_MS = 60 * 60 * 1000;
let getMicrosoft365UpsellDimeConfigInternal: GetMicrosoft365UpsellDimeConfig | undefined;
let loadMicrosoft365UpsellDimeConfigPromise: Promise<GetMicrosoft365UpsellDimeConfig> | undefined;

export function isMicrosoft365UpgradeMode(mode: Microsoft365UpsellMode): boolean {
    return mode === "upgradeBasic" || mode === "upgradePersonalOrFamily";
}

/**
 * Single source of truth for the Microsoft 365 header upsell. Visibility, label, tooltip, and the
 * launched Dime experience all derive from this so they can never disagree (e.g. showing "Buy" copy
 * while routing an "Upgrade" click).
 *
 * The upgrade modes take precedence over `buy`. Their subscription selector is cached after the
 * header's asynchronous visibility check initializes each account, keeping the premium
 * subscription chain out of AppBoot while label, tooltip, and click routing remain synchronous.
 */
export function getMicrosoft365UpsellMode(mailboxInfo: MailboxInfo): Microsoft365UpsellMode {
    return getUpsellResource(mailboxInfo)?.mode ?? "hidden";
}

export function getMicrosoft365UpsellIrisContent(mailboxInfo: MailboxInfo): OutlookHeaderM365UpsellButtonContent | undefined {
    return getUpsellResource(mailboxInfo)?.irisContent;
}

export function getMicrosoft365UpsellDimeConfig(mailboxInfo: MailboxInfo): Microsoft365UpsellDimeConfig | undefined {
    return getUpsellResource(mailboxInfo)?.dimeConfig;
}

function resolveMicrosoft365UpsellMode(mailboxInfo: MailboxInfo, resource: Microsoft365UpsellResource, isBuyEnabled: boolean, isUpgradeEnabled: boolean): Microsoft365UpsellMode {
    const upgradeTier = resource.getUpgradeTier?.(mailboxInfo);
    if (isUpgradeEnabled) {
        if (upgradeTier === "basic") {
            return "upgradeBasic";
        }
        if (upgradeTier === "personalOrFamily") {
            return "upgradePersonalOrFamily";
        }
    }
    
    // A forced post-purchase refresh loads subscription state even when the Upgrade flight is
    // disabled. Any active Microsoft 365 subscription suppresses the stale Buy offer.
    if (resource.hasActiveMicrosoft365Subscription?.(mailboxInfo)) {
        return "hidden";
    }
    
    if (isBuyEnabled && shouldShowUpsellOption(mailboxInfo, false /* isMSQ */)) {
        return "buy";
    }
    
    return "hidden";
}

export function isMicrosoft365UpsellVisible(mailboxInfo: MailboxInfo): boolean | Promise<boolean> {
    const cachedResource = getUpsellResource(mailboxInfo);
    hideCachedUpsell(mailboxInfo);
    getMicrosoft365UpsellRefreshStore().refreshVersionByAccount.get(getAccountKeyForMailboxInfo(mailboxInfo));
    
    // Only consumer accounts are ever offered the upsell; short-circuit before checking any flight
    // or loading the eligibility chunk for non-consumer accounts.
    if (!isConsumer(undefined /* smtpAddress */, mailboxInfo) ||
        isUnifiedInboxMailboxInfo(mailboxInfo)) {
        if (cachedResource) {
            clearMicrosoft365UpsellIrisRefresh(cachedResource);
        }
        return false;
    }
    
    const isUpgradeEnabled = isGlobalFeatureEnabled("header-persistentUpgradeM365Upsell");
    const isBuyEnabled = isGlobalFeatureEnabled("header-persistentBuyM365Upsell");
    if (!isUpgradeEnabled && !isBuyEnabled) {
        if (cachedResource) {
            clearMicrosoft365UpsellIrisRefresh(cachedResource);
        }
        return false;
    }
    
    if (!areAllLoggedInMailboxesConsumers()) {
        if (cachedResource) {
            clearMicrosoft365UpsellIrisRefresh(cachedResource);
        }
        return false;
    }
    
    const resource = getOrCreateUpsellResource(mailboxInfo);
    
    if (isUpgradeEnabled || resource.forceEligibilityRefresh) {
        if (resource.getUpgradeTier && !resource.forceEligibilityRefresh) {
            return resolveMicrosoft365UpsellVisibility(mailboxInfo, resource, isBuyEnabled, isUpgradeEnabled);
        }
        
        if (!resource.visibilityPromise) {
            resource.visibilityPromise = loadUpgradeResource(mailboxInfo, resource, isUpgradeEnabled);
        }
        
        return resource.visibilityPromise;
    }
    
    return resolveMicrosoft365UpsellVisibility(mailboxInfo, resource, isBuyEnabled, isUpgradeEnabled);
}

function resolveMicrosoft365UpsellVisibility(mailboxInfo: MailboxInfo, resource: Microsoft365UpsellResource, isBuyEnabled: boolean, isUpgradeEnabled: boolean): boolean | Promise<boolean> {
    resource.mode = resolveMicrosoft365UpsellMode(mailboxInfo, resource, isBuyEnabled, isUpgradeEnabled);
    if (resource.mode === "hidden") {
        clearMicrosoft365UpsellIrisRefresh(resource);
        resource.dimeConfig = undefined;
        return false;
    }
    if (!isMicrosoft365UpsellIrisEnabled()) {
        clearMicrosoft365UpsellIrisRefresh(resource);
        resource.irisContent = undefined;
        resource.irisContentPromise = undefined;
        return resolveMicrosoft365UpsellDimeConfig(resource);
    }
    
    if (!resource.irisContentPromise) {
        resource.irisContentPromise = loadMicrosoft365UpsellIrisContent(mailboxInfo, resource);
    }
    return resource.irisContentPromise.then(() => {
        ensureMicrosoft365UpsellIrisRefreshScheduled(mailboxInfo, resource);
        return resolveMicrosoft365UpsellDimeConfig(resource);
    });
}

function isMicrosoft365UpsellIrisEnabled(): boolean {
    return (isGlobalFeatureEnabled("growth-irisforpersistentheaderupsell") ||
        isGlobalFeatureEnabled("growth-irisforpersistentheaderupsell-test"));
}

function loadMicrosoft365UpsellIrisContent(mailboxInfo: MailboxInfo, resource: Microsoft365UpsellResource): Promise<void> {
    return (async (): Promise<void> => {
        try {
            const hasIrisContent = await lazyLoadOutlookHeaderM365UpsellButtonIrisContent.importAndExecute(mailboxInfo);
            if (hasIrisContent && isMicrosoft365UpsellIrisEnabled()) {
                const getIrisContent = await lazyGetOutlookHeaderM365UpsellButtonIrisContent.import();
                resource.irisContent = getIrisContent(mailboxInfo);
                scheduleMicrosoft365UpsellIrisRefresh(mailboxInfo, resource);
            }
        }
        catch {
            handleMicrosoft365UpsellIrisLoadFailure(mailboxInfo);
        }
    })();
}

function scheduleMicrosoft365UpsellIrisRefresh(mailboxInfo: MailboxInfo, resource: Microsoft365UpsellResource): void {
    clearMicrosoft365UpsellIrisRefresh(resource);
    if (resource.mode === "hidden") {
        return;
    }
    
    resource.irisRefreshTimeoutId = window.setTimeout(() => {
        resource.irisRefreshTimeoutId = undefined;
        if (document.visibilityState === "visible") {
            refreshMicrosoft365UpsellIrisContent(mailboxInfo, resource);
            return;
        }
        
        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                document.removeEventListener("visibilitychange", onVisibilityChange);
                resource.irisVisibilityChangeHandler = undefined;
                refreshMicrosoft365UpsellIrisContent(mailboxInfo, resource);
            }
        };
        resource.irisVisibilityChangeHandler = onVisibilityChange;
        document.addEventListener("visibilitychange", onVisibilityChange);
    }, IRIS_CONTENT_REFRESH_INTERVAL_MS);
}

function ensureMicrosoft365UpsellIrisRefreshScheduled(mailboxInfo: MailboxInfo, resource: Microsoft365UpsellResource): void {
    if (resource.mode === "hidden" ||
        !resource.irisContent ||
        resource.irisRefreshTimeoutId !== undefined ||
        resource.irisVisibilityChangeHandler ||
        resource.irisRefreshPromise) {
        return;
    }
    
    scheduleMicrosoft365UpsellIrisRefresh(mailboxInfo, resource);
}

function refreshMicrosoft365UpsellIrisContent(mailboxInfo: MailboxInfo, resource: Microsoft365UpsellResource): void {
    if (resource.mode === "hidden" || resource.irisRefreshPromise) {
        return;
    }
    
    const refreshPromise = (async () => {
        try {
            const hasIrisContent = await lazyRefreshOutlookHeaderM365UpsellButtonIrisContent.importAndExecute(mailboxInfo);
            if (!hasIrisContent || !isMicrosoft365UpsellIrisEnabled()) {
                return;
            }
            
            const getIrisContent = await lazyGetOutlookHeaderM365UpsellButtonIrisContent.import();
            resource.irisContent = getIrisContent(mailboxInfo);
            await resolveMicrosoft365UpsellDimeConfig(resource);
            incrementMicrosoft365UpsellRefreshVersionMutator(mailboxInfo);
        }
        catch {
            handleMicrosoft365UpsellIrisLoadFailure(mailboxInfo);
        }
        finally {
            if (resource.mode !== "hidden" &&
                resource.irisContent &&
                isMicrosoft365UpsellIrisEnabled()) {
                scheduleMicrosoft365UpsellIrisRefresh(mailboxInfo, resource);
            }
        }
    })();
    resource.irisRefreshPromise = refreshPromise;
    refreshPromise.finally(() => {
        if (resource.irisRefreshPromise === refreshPromise) {
            resource.irisRefreshPromise = undefined;
        }
    });
}

function clearMicrosoft365UpsellIrisRefresh(resource: Microsoft365UpsellResource): void {
    if (resource.irisRefreshTimeoutId !== undefined) {
        window.clearTimeout(resource.irisRefreshTimeoutId);
        resource.irisRefreshTimeoutId = undefined;
    }
    if (resource.irisVisibilityChangeHandler) {
        document.removeEventListener("visibilitychange", resource.irisVisibilityChangeHandler);
        resource.irisVisibilityChangeHandler = undefined;
    }
}

function handleMicrosoft365UpsellIrisLoadFailure(mailboxInfo: MailboxInfo): void {
    logUsage("HeaderMicrosoft365UpsellIrisLoadFailed", mailboxInfo, getGrowthTelemetryData({
        Scenario: "Microsoft365Upsell",
        Action: "ContentLoadFailed",
        EntryPoint: "AppHeader",
        PartnerId: "OutlookOwa",
        ActionSuccess: false,
        ActionErrorCategory: "IrisLoadFailed",
    }), { eventArea: "GrowthStandardTelemetry" });
}

function resolveMicrosoft365UpsellDimeConfig(resource: Microsoft365UpsellResource): boolean | Promise<boolean> {
    if (resource.mode === "hidden") {
        return false;
    }
    
    if (getMicrosoft365UpsellDimeConfigInternal) {
        resource.dimeConfig = getMicrosoft365UpsellDimeConfigInternal(resource.mode, resource.irisContent);
        return true;
    }
    
    if (!loadMicrosoft365UpsellDimeConfigPromise) {
        loadMicrosoft365UpsellDimeConfigPromise = lazyGetMicrosoft365UpsellDimeConfig
            .import()
            .then(getDimeConfig => {
            getMicrosoft365UpsellDimeConfigInternal = getDimeConfig;
            return getDimeConfig;
        })
            .catch(error => {
            loadMicrosoft365UpsellDimeConfigPromise = undefined;
            throw error;
        });
    }
    
    return loadMicrosoft365UpsellDimeConfigPromise.then(getDimeConfig => {
        if (resource.mode === "hidden") {
            return false;
        }
        resource.dimeConfig = getDimeConfig(resource.mode, resource.irisContent);
        return true;
    });
}

function areAllLoggedInMailboxesConsumers(): boolean {
    const accounts = getAccounts(AccountStatus.Any, App.All);
    return (accounts.length > 0 &&
        accounts.every(account => account.bootState === BootState.StartupComplete &&
            isConsumer(undefined /* smtpAddress */, account.mailboxInfo)));
}

function getUpsellResource(mailboxInfo: MailboxInfo): Microsoft365UpsellResource | undefined {
    return upsellResources.get(getAccountKeyForMailboxInfo(mailboxInfo));
}

function hideCachedUpsell(mailboxInfo: MailboxInfo): void {
    if (upsellResources.size === 0) {
        return;
    }
    
    const resource = getUpsellResource(mailboxInfo);
    if (resource) {
        resource.mode = "hidden";
        resource.dimeConfig = undefined;
    }
}

function getOrCreateUpsellResource(mailboxInfo: MailboxInfo): Microsoft365UpsellResource {
    const mailboxKey = getAccountKeyForMailboxInfo(mailboxInfo);
    let resource = upsellResources.get(mailboxKey);
    if (!resource) {
        resource = { eligibilityGeneration: 0, mode: "hidden" };
        upsellResources.set(mailboxKey, resource);
    }
    return resource;
}

function loadUpgradeResource(mailboxInfo: MailboxInfo, resource: Microsoft365UpsellResource, isUpgradeEnabled: boolean): Promise<boolean> {
    const eligibilityGeneration = resource.eligibilityGeneration;
    const forceEligibilityRefresh = resource.forceEligibilityRefresh ?? false;
    const visibilityPromise = (async () => {
        const fetchEligibility = await lazyFetchMicrosoft365UpgradeEligibility.import();
        await fetchEligibility(mailboxInfo, forceEligibilityRefresh);
        if (resource.eligibilityGeneration !== eligibilityGeneration) {
            return false;
        }
        
        const [getUpgradeTier, hasActiveMicrosoft365Subscription] = await Promise.all([
            lazyGetMicrosoft365UpgradeTier.import(),
            lazyHasActiveMicrosoft365Subscription.import(),
        ]);
        if (resource.eligibilityGeneration !== eligibilityGeneration) {
            return false;
        }
        resource.forceEligibilityRefresh = false;
        resource.getUpgradeTier = getUpgradeTier;
        resource.hasActiveMicrosoft365Subscription = hasActiveMicrosoft365Subscription;
        return resolveMicrosoft365UpsellVisibility(mailboxInfo, resource, isGlobalFeatureEnabled("header-persistentBuyM365Upsell"), isUpgradeEnabled);
    })();
    
    visibilityPromise.catch(() => {
        if (resource.visibilityPromise === visibilityPromise) {
            resource.visibilityPromise = undefined;
            if (!resource.forceEligibilityRefresh) {
                upsellResources.delete(getAccountKeyForMailboxInfo(mailboxInfo));
            }
        }
    });
    
    return visibilityPromise;
}

const incrementMicrosoft365UpsellRefreshVersionMutator = mutatorAction("incrementMicrosoft365UpsellRefreshVersion", (mailboxInfo: MailboxInfo): void => {
    const mailboxKey = getAccountKeyForMailboxInfo(mailboxInfo);
    const refreshVersionByAccount = getMicrosoft365UpsellRefreshStore().refreshVersionByAccount;
    refreshVersionByAccount.set(mailboxKey, (refreshVersionByAccount.get(mailboxKey) ?? 0) + 1);
});

export function invalidateMicrosoft365UpsellEligibilityAfterPurchase(mailboxInfo: MailboxInfo): void {
    const resource = getOrCreateUpsellResource(mailboxInfo);
    clearMicrosoft365UpsellIrisRefresh(resource);
    resource.eligibilityGeneration++;
    resource.forceEligibilityRefresh = true;
    resource.getUpgradeTier = undefined;
    resource.hasActiveMicrosoft365Subscription = undefined;
    
    resource.visibilityPromise = undefined;
    resource.mode = "hidden";
    resource.dimeConfig = undefined;
    
    incrementMicrosoft365UpsellRefreshVersionMutator(mailboxInfo);
}

const resetMicrosoft365UpsellRefreshStoreForTestMutator = mutatorAction("resetMicrosoft365UpsellRefreshStoreForTest", (): void => {
    getMicrosoft365UpsellRefreshStore().refreshVersionByAccount.clear();
});

export function resetMicrosoft365UpgradeResourcesForTest(): void {
    for (const resource of upsellResources.values()) {
        clearMicrosoft365UpsellIrisRefresh(resource);
    }
    upsellResources.clear();
    getMicrosoft365UpsellDimeConfigInternal = undefined;
    loadMicrosoft365UpsellDimeConfigPromise = undefined;
    resetMicrosoft365UpsellRefreshStoreForTestMutator();
}
